import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Clock, X, CheckCircle, AlertCircle, Layers } from 'lucide-react';
import axios from 'axios';
import { getAuthHeaders } from '../../context/AuthContext';
import Modal from '../../components/Modal';

const API = process.env.REACT_APP_BACKEND_URL;

function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-lg text-sm font-body animate-fade-in-up
      ${type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
      {type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
      {message}
    </div>
  );
}

const EMPTY_FORM = { name: '', start_time: '', end_time: '' };

export default function AdminBatches() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editBatch, setEditBatch] = useState(null); // batch object being edited
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  const fetch = async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/batches`, { headers: getAuthHeaders() });
      setBatches(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const openAdd = () => { setEditBatch(null); setForm(EMPTY_FORM); setShowForm(true); };
  const openEdit = (b) => { setEditBatch(b); setForm({ name: b.name, start_time: b.start_time, end_time: b.end_time }); setShowForm(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.start_time || !form.end_time) {
      setToast({ message: 'Please fill in all fields.', type: 'error' }); return;
    }
    setSubmitting(true);
    try {
      if (editBatch) {
        await axios.put(`${API}/api/admin/batches/${editBatch.id}`, form, { headers: getAuthHeaders() });
        setToast({ message: `"${form.name}" updated!`, type: 'success' });
      } else {
        await axios.post(`${API}/api/admin/batches`, form, { headers: getAuthHeaders() });
        setToast({ message: `"${form.name}" created!`, type: 'success' });
      }
      setShowForm(false);
      fetch();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setToast({ message: typeof detail === 'string' ? detail : 'Something went wrong.', type: 'error' });
    } finally { setSubmitting(false); }
  };

  const fmt = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':');
    const hr = parseInt(h, 10);
    return `${hr % 12 || 12}:${m} ${hr < 12 ? 'AM' : 'PM'}`;
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-[#F59E0B] font-body mb-1">Manage</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#292524]">Batches</h1>
          <p className="text-[#57534E] text-sm font-body mt-1">Create and manage class time slots.</p>
        </div>
        <button
          data-testid="add-batch-button"
          onClick={openAdd}
          className="flex items-center gap-2 bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full px-5 py-2.5 text-sm font-heading font-medium shadow-button hover:-translate-y-0.5 transition-all"
        >
          <Plus className="w-4 h-4" strokeWidth={2} /> Add Batch
        </button>
      </div>

      {showForm && (
        <Modal
          title={editBatch ? 'Edit Batch' : 'New Batch'}
          onClose={() => setShowForm(false)}
          footer={
            <div className="flex gap-3">
              <button type="button" onClick={() => setShowForm(false)}
                className="flex-1 border-2 border-[#E7E5E4] text-[#57534E] rounded-full py-3 font-heading font-medium text-sm hover:border-[#F59E0B] transition-colors">
                Cancel
              </button>
              <button data-testid="submit-batch" type="submit" form="batch-form" disabled={submitting}
                className="flex-1 bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full py-3 font-heading font-medium text-sm shadow-button disabled:opacity-60 transition-all">
                {submitting ? 'Saving...' : editBatch ? 'Update' : 'Create Batch'}
              </button>
            </div>
          }
        >
          <form id="batch-form" onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#57534E] mb-2">Batch Name</label>
              <input
                data-testid="batch-name-input"
                type="text" placeholder='e.g. "7 AM Batch"'
                value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="bg-white border-2 border-[#E7E5E4] rounded-2xl px-4 py-3 focus:ring-4 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B] outline-none w-full text-[#292524] font-body"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-[#57534E] mb-2">Start Time</label>
                <input
                  data-testid="batch-start-time-input"
                  type="time" value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))}
                  className="bg-white border-2 border-[#E7E5E4] rounded-2xl px-4 py-3 focus:ring-4 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B] outline-none w-full text-[#292524] font-body"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#57534E] mb-2">End Time</label>
                <input
                  data-testid="batch-end-time-input"
                  type="time" value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))}
                  className="bg-white border-2 border-[#E7E5E4] rounded-2xl px-4 py-3 focus:ring-4 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B] outline-none w-full text-[#292524] font-body"
                />
              </div>
            </div>
          </form>
        </Modal>
      )}

      {/* Batch list */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="w-10 h-10 border-4 border-[#F59E0B] border-t-transparent rounded-full animate-spin" /></div>
      ) : batches.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 text-center shadow-card border border-[#E7E5E4]/50">
          <div className="w-16 h-16 bg-[#FFEDD5] rounded-full flex items-center justify-center mx-auto mb-4">
            <Layers className="w-8 h-8 text-[#F59E0B]" strokeWidth={1.5} />
          </div>
          <p className="font-heading text-lg font-medium text-[#292524]">No batches yet</p>
          <p className="text-[#78716C] text-sm font-body mt-1">Create your first batch to start assigning students.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {batches.map(b => (
            <div key={b.id} data-testid="batch-card"
              className="bg-white rounded-3xl p-6 shadow-card border border-[#E7E5E4]/50 hover:shadow-card-hover transition-all duration-300">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="w-10 h-10 bg-[#FFEDD5] rounded-2xl flex items-center justify-center mb-3">
                    <Layers className="w-5 h-5 text-[#F59E0B]" strokeWidth={1.5} />
                  </div>
                  <p className="font-heading font-semibold text-[#292524] text-base">{b.name}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Clock className="w-3.5 h-3.5 text-[#A8A29E]" strokeWidth={1.5} />
                    <p className="text-sm text-[#78716C] font-body">{fmt(b.start_time)} – {fmt(b.end_time)}</p>
                  </div>
                </div>
                <button
                  data-testid={`edit-batch-${b.id}`}
                  onClick={() => openEdit(b)}
                  className="text-[#A8A29E] hover:text-[#F59E0B] transition-colors p-1 shrink-0"
                >
                  <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
